/**
 * O que faz: Impede o acesso ao CRM enquanto a clínica não possui um plano pago ou parceiro ativo.
 * Onde usa: Rotas privadas do CRM e ferramentas autenticadas fora do layout principal.
 * Conexões: ClinicaContext, usePlanPermissions e React Router.
 * Tabelas: subscriptions e clinicas, lidas indiretamente pelo hook usePlanPermissions.
 * Data: 23/07/2026.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { useClinica } from '@/contexts/ClinicaContext';

interface SubscriptionPaymentGateProps {
  children: React.ReactNode;
}

// Planos com acesso liberado ao CRM (PMK é parceria e deve entrar no dashboard normalmente).
const PLANOS_COM_ACESSO = new Set(['basic', 'pro', 'premium', 'pmk', 'professional']);

export const SubscriptionPaymentGate = ({ children }: SubscriptionPaymentGateProps) => {
  const location = useLocation();
  const { isLoading: clinicaLoading } = useClinica();
  const { isAdmin, planName, isLoading: planoLoading } = usePlanPermissions();

  // Aguarda clínica + plano reais. Se liberar cedo, planName cai em "free" e redireciona
  // para /billing por engano (mesmo com PMK/pago), e o usuário fica preso nessa rota.
  // Não usar !clinicaAtiva como loading eterno: usuário sem clínica precisa sair do spinner.
  if (clinicaLoading || planoLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        {/* Mesmo padrão visual do auth: solving enquanto resolve plano/acesso */}
        <ThinkingOrbLoader state="solving" size={64} label="Verificando sua assinatura..." />
      </div>
    );
  }

  const planoNormalizado = String(planName || 'free').trim().toLowerCase();
  const possuiAcesso = isAdmin || PLANOS_COM_ACESSO.has(planoNormalizado);

  // Cobrança continua acessível para quem ainda precisa pagar.
  if (!possuiAcesso && location.pathname !== '/billing') {
    return (
      <Navigate
        to="/billing"
        state={{ assinaturaObrigatoria: true, origem: location.pathname }}
        replace
      />
    );
  }

  return <>{children}</>;
};
